declare global {
  interface Window {
    FB?: any;
    fbAsyncInit?: () => void;
  }
}

let loaded: Promise<any> | null = null;
let loadedAppId = '';
let loadedVersion = '';
let signupInProgress = false;

export function loadFacebookSdk(appId: string, graphVersion = 'v26.0'): Promise<any> {
  if (!appId) return Promise.reject(new Error('Facebook connection is not configured.'));
  if (loaded) return loadedAppId === appId && loadedVersion === graphVersion ? loaded : Promise.reject(new Error('Facebook app configuration changed. Reload this page and try again.'));
  loadedAppId = appId;
  loadedVersion = graphVersion;
  loaded = new Promise((resolve, reject) => {
    let script: HTMLScriptElement | null = null;
    const timer = window.setTimeout(() => fail(), 15_000);
    const fail = () => {
      window.clearTimeout(timer);
      script?.remove();
      reject(new Error('Facebook could not load. Check your connection or browser content blocker and try again.'));
    };
    const initialize = () => {
      try {
        window.FB!.init({ appId, autoLogAppEvents: false, xfbml: false, version: graphVersion });
        window.clearTimeout(timer);
        resolve(window.FB);
      } catch { fail(); }
    };
    if (window.FB) { initialize(); return; }
    window.fbAsyncInit = initialize;
    script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.onerror = fail;
    document.body.appendChild(script);
  }).catch(error => { loaded = null; loadedAppId = ''; throw error; });
  return loaded;
}

export interface EmbeddedSignupResult {
  code: string;
  wabaId: string;
  phoneNumberId: string;
}

export function launchEmbeddedSignup(configId: string, signal?: AbortSignal): Promise<EmbeddedSignupResult> {
  if (signupInProgress) return Promise.reject(new Error('A Facebook connection is already in progress.'));
  return new Promise((resolve, reject) => {
    if (!window.FB || !configId) { reject(new Error('Facebook connection is not ready. Please try again.')); return; }
    if (signal?.aborted) { reject(new Error('Sign-in cancelled')); return; }
    signupInProgress = true;
    let code = '';
    let signupData: { waba_id: string; phone_number_id: string } | null = null;
    let settled = false;
    let detailTimer: number | undefined;
    const timeout = window.setTimeout(() => finish(new Error('Facebook signup timed out. Please try again.')), 10 * 60_000);
    const cleanup = () => {
      window.clearTimeout(timeout);
      window.clearTimeout(detailTimer);
      window.removeEventListener('message', onMessage);
      signal?.removeEventListener('abort', onAbort);
      signupInProgress = false;
    };
    const finish = (error?: Error) => {
      if (settled || (!error && (!code || !signupData))) return;
      settled = true;
      cleanup();
      if (error) reject(error);
      else resolve({ code, wabaId: signupData!.waba_id, phoneNumberId: signupData!.phone_number_id });
    };
    const onAbort = () => finish(new Error('Sign-in cancelled'));
    const onMessage = (event: MessageEvent) => {
      if (!['https://www.facebook.com', 'https://web.facebook.com'].includes(event.origin)) return;
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data?.type !== 'WA_EMBEDDED_SIGNUP') return;
        if (data.event === 'CANCEL') { finish(new Error('Sign-in cancelled')); return; }
        if (data.event === 'ERROR') { finish(new Error('Facebook could not complete signup. Please retry or use manual connection.')); return; }
        if (data.event === 'FINISH') {
          if (!/^\d+$/.test(data.data?.waba_id ?? '') || !/^\d+$/.test(data.data?.phone_number_id ?? '')) {
            finish(new Error('Select a WhatsApp Business Account and phone number in Facebook before finishing.'));
            return;
          }
          signupData = { waba_id: String(data.data.waba_id), phone_number_id: String(data.data.phone_number_id) };
          finish();
        } else if (typeof data.event === 'string' && data.event.startsWith('FINISH_')) {
          finish(new Error('This signup configuration uses a different onboarding flow. Ask your administrator for a Cloud API configuration with phone-number selection.'));
        }
      } catch { /* Ignore unrelated or malformed popup messages. */ }
    };
    window.addEventListener('message', onMessage);
    signal?.addEventListener('abort', onAbort, { once: true });
    try {
      window.FB.login((response: any) => {
        if (settled) return;
        if (!response.authResponse?.code) { finish(new Error('Sign-in cancelled or Facebook did not return authorization.')); return; }
        code = response.authResponse.code;
        // Facebook delivers authorization and the selected assets independently, in either order.
        if (!signupData) detailTimer = window.setTimeout(() => finish(new Error('Facebook did not return the selected number. Please retry signup and complete the phone selection.')), 15_000);
        finish();
      }, {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: { setup: {} },
      });
    } catch { finish(new Error('Facebook popup could not open. Allow popups for this site and try again.')); }
  });
}
